import { type ReactNode } from 'react';
import { Loader2, Save, X, Settings2 } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { getCapabilityColors } from '@/lib/capability-colors';
import type { CapabilityMethod, CapabilityRevision, CapabilityRevisionFile, CapabilityDefault } from '@/types';
import {
   CapabilityOverviewTab,
   type OverviewPersistMode,
   type DeferredOverviewDirtyPayload,
} from './CapabilityOverviewTab';
import { RevisionsTab, type WorkingCopyEntityData } from './RevisionsTab';
import { MethodsTab } from './MethodsTab';
import { FilesTab } from './FilesTab';
import { DefaultsTab } from './DefaultsTab';
import { DeploymentTab } from './DeploymentTab';
import { CapabilityGrantsPanel } from './CapabilityGrantsPanel';
import { CapabilityBindingsPanel } from './CapabilityBindingsPanel';

export interface CapabilitySettingsSheetProps {
   isOpen: boolean;
   onClose: () => void;
   data: Record<string, unknown>;
   typeLabel: string;
   overviewPersistMode: OverviewPersistMode;
   onOverviewDeferredDirtyChange?: (payload: DeferredOverviewDirtyPayload) => void;
   /** Parent-owned overview draft restored when the sheet remounts. */
   pendingOverviewPatch?: Record<string, unknown>;
   overviewFormDisabled: boolean;
   overviewChildren?: ReactNode;
   hasWorkingRevision: boolean;
   /** When deferred overview is dirty, show Save in the sheet footer. */
   onSaveDeferred?: () => void;
   isOverviewDirty?: boolean;
   isSaving?: boolean;
}

export function CapabilitySettingsSheet({
   isOpen,
   onClose,
   data,
   typeLabel,
   overviewPersistMode,
   onOverviewDeferredDirtyChange,
   pendingOverviewPatch,
   overviewFormDisabled,
   overviewChildren,
   hasWorkingRevision,
   onSaveDeferred,
   isOverviewDirty = false,
   isSaving = false,
}: CapabilitySettingsSheetProps) {
   const capType = (data as { type?: string }).type ?? '';
   const colors = getCapabilityColors(capType);
   const Icon = colors.icon;
   const entityName = (data as { name?: string }).name ?? '';

   const entityData: WorkingCopyEntityData | undefined = hasWorkingRevision
      ? {
           name: (data as { name: string }).name,
           app_key: (data as { app_key: string }).app_key,
           fqcn: (data as { fqcn: string }).fqcn,
           display_name: (data as { display_name?: string }).display_name,
           type: (data as { type: string }).type,
           description: (data as { description?: string }).description,
           tags: (data as { tags?: string[] }).tags,
           visibility: (data as { visibility?: string }).visibility,
           aliases: (data as { aliases?: string[] }).aliases,
        }
      : undefined;

   return (
      <SlideDrawer isOpen={isOpen} onClose={onClose}>
                  {/* Header */}
                  <div className="px-10 py-4 border-b border-slate-100 bg-slate-50/50">
                     <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                           <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${colors.bgLight}`}>
                              <Icon size={20} className={colors.text} />
                           </div>
                           <div>
                              <div className="flex items-center gap-2">
                                 <h2 className="text-xl font-semibold text-slate-900">Settings</h2>
                                 <Settings2 size={16} className="text-slate-400" />
                              </div>
                              <p className="text-sm text-slate-500 mt-0.5">{entityName}</p>
                           </div>
                        </div>
                        <button
                           onClick={onClose}
                           className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                           <X size={20} />
                        </button>
                     </div>
                  </div>

                  {/* Tabbed Content */}
                  <div className="flex-1 overflow-hidden flex flex-col">
                     <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden">
                        <div className="border-b border-slate-200 px-10">
                           <TabsList className="bg-transparent h-auto p-0">
                              <TabsTrigger value="overview" className="px-4 py-3">Overview</TabsTrigger>
                              <TabsTrigger value="methods" className="px-4 py-3">Methods</TabsTrigger>
                              <TabsTrigger value="files" className="px-4 py-3">Artifacts</TabsTrigger>
                              <TabsTrigger value="revisions" className="px-4 py-3">Revisions</TabsTrigger>
                              <TabsTrigger value="defaults" className="px-4 py-3">Defaults</TabsTrigger>
                              <TabsTrigger value="access" className="px-4 py-3">Access</TabsTrigger>
                              <TabsTrigger value="deployment" className="px-4 py-3">Deployment</TabsTrigger>
                           </TabsList>
                        </div>

                        <div className="flex-1 overflow-y-auto px-10 py-6">
                           <TabsContent value="overview" className="mt-0">
                              <CapabilityOverviewTab
                                 data={data as Parameters<typeof CapabilityOverviewTab>[0]['data']}
                                 persistMode={overviewPersistMode}
                                 onDeferredDirtyChange={onOverviewDeferredDirtyChange}
                                 pendingPatch={pendingOverviewPatch}
                                 formDisabled={overviewFormDisabled}
                              />
                              {overviewChildren}
                           </TabsContent>

                           <TabsContent value="methods" className="mt-0">
                              <MethodsTab
                                 methods={
                                    (data.methods as CapabilityMethod[] | undefined) ??
                                    (data.working_revision as { methods?: CapabilityMethod[] } | undefined)?.methods
                                 }
                              />
                           </TabsContent>

                           <TabsContent value="files" className="mt-0">
                              <FilesTab
                                 files={
                                    (data.files as CapabilityRevisionFile[] | undefined) ??
                                    (data.working_revision as { files?: CapabilityRevisionFile[] } | undefined)?.files
                                 }
                                 entityData={entityData}
                                 workingRevisionTypeConfig={
                                    hasWorkingRevision
                                       ? (data.working_revision as { type_config?: Record<string, unknown> } | undefined)?.type_config
                                       : undefined
                                 }
                              />
                           </TabsContent>

                           <TabsContent value="revisions" className="mt-0">
                              <RevisionsTab
                                 workingRevision={data.working_revision as CapabilityRevision | null | undefined}
                                 immutableRevisions={data.immutable_revisions as CapabilityRevision[] | undefined}
                                 entityData={{
                                    name: data.name as string,
                                    app_key: data.app_key as string,
                                    fqcn: data.fqcn as string,
                                    display_name: data.display_name as string | undefined,
                                    type: data.type as string,
                                    description: data.description as string | undefined,
                                    tags: data.tags as string[] | undefined,
                                    visibility: data.visibility as string | undefined,
                                    aliases: data.aliases as string[] | undefined,
                                 } satisfies WorkingCopyEntityData}
                              />
                           </TabsContent>

                           <TabsContent value="defaults" className="mt-0">
                              <DefaultsTab defaults={data.defaults as CapabilityDefault[] | undefined} />
                           </TabsContent>

                           <TabsContent value="access" className="mt-0">
                              <div className="flex flex-col gap-6">
                                 <CapabilityGrantsPanel
                                    appKey={(data as { app_key?: string }).app_key ?? ''}
                                    capabilityName={(data as { name?: string }).name ?? ''}
                                    visibility={(data as { visibility?: string }).visibility}
                                 />
                                 <CapabilityBindingsPanel
                                    appKey={(data as { app_key?: string }).app_key ?? ''}
                                    capabilityName={(data as { name?: string }).name ?? ''}
                                 />
                              </div>
                           </TabsContent>

                           <TabsContent value="deployment" className="mt-0">
                              <DeploymentTab cap={(data.fqcn as string) ?? ''} />
                           </TabsContent>
                        </div>
                     </Tabs>
                  </div>

                  {overviewPersistMode === 'deferred' && onSaveDeferred && (
                     <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/80 px-10 py-4">
                        <p className="mr-auto text-xs text-slate-500">
                           Overview changes are saved with the header Save action.
                        </p>
                        <Button type="button" variant="outline" onClick={onClose}>
                           Close
                        </Button>
                        <Button
                           type="button"
                           onClick={() => onSaveDeferred()}
                           disabled={!isOverviewDirty || isSaving || overviewFormDisabled}
                           className="inline-flex items-center gap-1.5"
                        >
                           {isSaving ? (
                              <Loader2 size={14} className="animate-spin" />
                           ) : (
                              <Save size={14} />
                           )}
                           Save
                        </Button>
                     </div>
                  )}
      </SlideDrawer>
   );
}
